'use client';

import {
  AppleLogo,
  CaretUp,
  FiveStarRating,
  PlayStoreLogo,
} from 'assets/icons';
import { H2 } from 'components/m1/StyleComponents';
import React, { useState, useRef, useEffect } from 'react';
import { Container, Content } from './LayoutComponents';
import CustomLink from 'components/shared/CustomLink';
import styles from './M1Reviews.module.css';

import appstoreRating from '../../assets/images/appstoreRating.svg';
import playStoreRating from '../../assets/images/playstoreRating.svg';

const AppStoreConfig = {
  apple: {
    rating: 4.9,
    reviews: 2000,
    title: 'App store',
    ratingIcon: appstoreRating,
    storeIcon: <AppleLogo />,
    ctaText: 'Download on the AppStore',
    ctaLink:
      'https://apps.apple.com/us/app/ultrahuman-meditation-sleep/id1491286709',
  },
  google: {
    rating: 4.5,
    reviews: 2000,
    title: 'Play store',
    ratingIcon: playStoreRating,
    storeIcon: <PlayStoreLogo />,
    ctaText: 'Download on PlayStore',
    ctaLink:
      'https://play.google.com/store/apps/details?id=com.ultrahuman.android',
  },
};

const UserReview = ({
  author,
  title,
  repeatUser,
  review,
  rating,
}: {
  className?: string;
  author: string;
  title: string;
  repeatUser?: boolean;
  review: string | React.ReactNode;
  rating: number;
}) => {
  return (
    <article className={styles.userReview}>
      <div className="section">
        <p className={styles.author}>{author}</p>
        <p className={styles.title}>{title}</p>
      </div>
      <div className={styles['rating-wrapper']}>
        <FiveStarRating rating={rating} />
        <p className={styles['repeat-user']}>
          {repeatUser ? 'Repeat purchase' : null}
        </p>
      </div>
      <q className="review">{review}</q>
    </article>
  );
};

const AppStoreReviews = ({ store }: { className?: string; store: 'apple' | 'google' }) => {
  const { rating, title, ctaLink, storeIcon, ctaText } = AppStoreConfig[store];
  return (
    <div className={styles.appStoreReviews}>
      <div className={styles.description}>
        <p className={styles.rating}>{rating}</p>
        <div className={styles.content}>
          <p className={styles.title}>{title}</p>
          <FiveStarRating rating={rating} />
        </div>
      </div>
      <CustomLink href={ctaLink}>
        {storeIcon} <span>{ctaText}</span>
      </CustomLink>
    </div>
  );
};

export const M1Reviews = ({
  title,
  items,
}: {
  className?: string;
  title: string;
  app_store: Record<string, string | React.ReactNode>;
  play_store: Record<string, string | React.ReactNode>;
  items: ReadonlyArray<Record<string, string | number | React.ReactNode>>;
}) => {
  const [showMore, setShowMore] = useState(true);
  const [truncateReviews, setTruncateReviews] = useState(true);
  const [reviewsToShow, setReviewsToShow] = useState(0);
  const toggleShowMore = () => {
    setTruncateReviews((prev) => !prev);
    // setShowMore((prev) => !prev);
  };
  const ref = useRef<HTMLDivElement>(null);

  const calculateReviewsToShow = () => {
    const containerWidth = ref.current?.clientWidth ?? 0;
    const itemWidth = 400; // The min-width of the grid item
    let itemsPerRow = Math.floor(containerWidth / itemWidth);
    if (!itemsPerRow) itemsPerRow = 1;

    if (truncateReviews) {
      setReviewsToShow(itemsPerRow * 2);
      if (itemsPerRow * 2 < items.length) setShowMore(true);
    } else {
      setReviewsToShow(items.length);
      setShowMore(false);
    }
  };

  useEffect(() => {
    calculateReviewsToShow();
    window.addEventListener('resize', calculateReviewsToShow);
    return () => {
      window.removeEventListener('resize', calculateReviewsToShow);
    };
  }, [showMore, items.length, ref.current, truncateReviews]);

  const displayedReviews = items.slice(0, reviewsToShow);

  return (
    <Container fill="white">
      <Content>
        <div className={styles.root}>
          <H2 color="black" align="left">
            {title}
          </H2>
          <div className={styles['app-store-reviews']}>
            <AppStoreReviews store="apple" />
            <AppStoreReviews store="google" />
          </div>

          <div className={styles['user-reviews']} ref={ref}>
            {displayedReviews.map((review, index) => {
              return (
                <UserReview
                  key={index}
                  author={review.userName as string}
                  repeatUser={true as boolean}
                  title={review.title as string}
                  rating={review.starCount as number}
                  review={review.content as string | React.ReactNode}
                />
              );
            })}
            {items.length > 5 && (
              <button onClick={toggleShowMore}>
                {showMore ? 'Show more' : 'Show less'}{' '}
                <CaretUp className={showMore ? 'rotate' : ''} fill="black" />
              </button>
            )}
          </div>
        </div>
      </Content>
    </Container>
  );
};
